import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.js';

// All roles go to the new entry choice page by default
const getRoleRoute = (role) => '/jobs/new-entry';

export default function Login() {
  const [form, setForm]   = useState({ email:'', password:'', location:'' });
  const [locations, setLocations] = useState([]);
  const [show, setShow]   = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const { login, loading, error, clearError, user, token } = useAuth();

  useEffect(() => {
    axios.get('/api/jobs/locations')
      .then(({data}) => setLocations(data.locations || []))
      .catch(() => {});
  }, []);

  const navigate  = useNavigate();
  const location  = useLocation();
  const from = location.state?.from?.pathname || null;

  useEffect(() => {
    if (user && token && justLoggedIn) {
      navigate(getRoleRoute(user.role), { replace: true });
    }
  }, [user, token, justLoggedIn]);

  // If already logged in and visiting /login, redirect to home
  useEffect(() => {
    if (user && token && !justLoggedIn) {
      navigate(getRoleRoute(user.role), { replace: true });
    }
  }, []);

  const handleChange = (e) => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(form.email, form.password, form.location);
    if (result?.success) {
      setJustLoggedIn(true);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:'Inter,sans-serif' }}>
      <div style={{ width:'100%', maxWidth:'380px' }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <div style={{ width:'64px', height:'64px', background:'#000', borderRadius:'50%', margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <img src={logo} alt="Car Saloon" style={{ width:'56px', height:'56px', objectFit:'contain' }}/>
          </div>
          <h1 style={{ fontSize:'20px', fontWeight:'600', color:'var(--text)', margin:0 }}>Car Saloon</h1>
          <p style={{ fontSize:'13px', color:'var(--muted)', marginTop:'4px' }}>Staff Management Portal</p>
        </div>

        {/* Card */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'24px', boxShadow:'var(--shadow)' }}>

          {error && (
            <div style={{ background:'var(--red-bg)', border:'1px solid rgba(163,45,45,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', color:'var(--red)', fontSize:'13px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
              <i className="ti ti-alert-circle" style={{ flexShrink:0 }} aria-hidden="true"/>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', marginBottom:'5px' }}>Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required
                placeholder="you@carsaloon.com.au"
                style={{ width:'100%', background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'9px 12px', fontSize:'14px', color:'var(--text)', outline:'none' }}
                onFocus={e=>e.target.style.borderColor='var(--blue)'}
                onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
            </div>

            <div style={{ marginBottom:'20px' }}>
              <label style={{ display:'block', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', marginBottom:'5px' }}>Password</label>
              <div style={{ position:'relative' }}>
                <input name="password" type={show?'text':'password'} value={form.password} onChange={handleChange} required
                  placeholder="••••••••"
                  style={{ width:'100%', background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'9px 40px 9px 12px', fontSize:'14px', color:'var(--text)', outline:'none' }}
                  onFocus={e=>e.target.style.borderColor='var(--blue)'}
                  onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                <button type="button" onClick={() => setShow(s=>!s)}
                  style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--hint)', display:'flex', alignItems:'center', padding:'4px' }}>
                  <i className={`ti ${show?'ti-eye-off':'ti-eye'}`} style={{ fontSize:'16px' }} aria-label={show?'Hide password':'Show password'}/>
                </button>
              </div>
            </div>

            <div style={{ marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', marginBottom:'5px' }}>Your Location</label>
              <select name="location" value={form.location} onChange={handleChange}
                style={{ width:'100%', background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'9px 12px', fontSize:'14px', color:form.location?'var(--text)':'var(--hint)', outline:'none', fontFamily:'inherit' }}
                onFocus={e=>e.target.style.borderColor='var(--blue)'}
                onBlur={e=>e.target.style.borderColor='var(--border-md)'}>
                <option value="">Select your location...</option>
                {locations.map(l=><option key={l._id} value={l.name}>{l.name}</option>)}
              </select>
            </div>

            <button type="submit" disabled={loading}
              style={{ width:'100%', padding:'11px', background:loading?'var(--hint)':'var(--blue)', border:'none', borderRadius:'var(--radius)', color:'#fff', fontSize:'14px', fontWeight:'500', cursor:loading?'not-allowed':'pointer', transition:'background .15s' }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        <p style={{ textAlign:'center', color:'var(--hint)', fontSize:'11px', marginTop:'20px' }}>
          © 2026 Car Saloon
        </p>
      </div>
    </div>
  );
}
